<template>
  <div>
    <rest-list-editable
      ref="rest-list-editable"
      :payload="fetchEndpoint"
      :update-endpoint="rowUpdateRoute"
      :rules="rules"
    >
      <rest-list-column
        prop="name"
        :title="$t('List_Field_Name')"
        :filterable="true"
        :route="rowViewRoute"
        :editable="true"
      />
      <template v-if="isSportradarList">
        <rest-list-column-date
          v-if="selectedColumns.start"
          prop="start"
          :title="$t('List_Field_Start')"
        />
        <rest-list-column-enum
          prop="sport"
          :title="$t('List_Field_Sport')"
          :editable="true"
          :enum="SportsLabel"
        />
        <rest-list-column
          v-if="selectedColumns.country"
          prop="country"
          :title="$t('List_Field_Country')"
          :filterable="true"
          :editable="true"
        />
        <rest-list-column
          v-if="selectedColumns.competition"
          prop="competition"
          :title="$t('List_Field_Competition')"
          :filterable="true"
          :editable="true"
        />
        <rest-list-column
          v-if="selectedColumns.competitor_home"
          prop="competitor_home"
          :title="$t('List_Field_Home')"
          :filterable="true"
          :editable="true"
        />
        <rest-list-column
          v-if="selectedColumns.competitor_away"
          prop="competitor_away"
          :title="$t('List_Field_Away')"
          :filterable="true"
          :editable="true"
        />
      </template>
      <rest-list-column-enum
        :editable="true"
        prop="identifier"
        :title="$t('List_Field_Identifier')"
        :enum="identifierList"
      />
      <rest-list-column-actions :editable="!isSportradarList" v-if="!readonly">
        <template v-if="isSportradarList" slot="header" slot-scope="{}">
          <rest-list-column-settings
            storage-key="sportfeed-list-columns"
            :title="$t('List_Field_Settings')"
            :columns="columns"
            v-model="selectedColumns"
          />
        </template>
        <template slot-scope="scope">
          <rest-list-action-view
            :route="rowViewRoute(scope.row)"
            :title="$t('Audiences_UserList_Action_View')"
          />
          <rest-list-action-delete
            :endpoint="rowDeleteEndpoint(scope.row)"
            :name="scope.row.name"
            v-if="!isSportradarList"
          />
        </template>
      </rest-list-column-actions>
    </rest-list-editable>

    <list-create-dialog
      v-if="createPopup"
      :on-close="closeCreatePopup"
      :suppress="suppress"
      :external-type="externalType"
    ></list-create-dialog>
  </div>
</template>

<script>
import { EventBus } from '@/core/EventBus'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListActionDelete from '@/common/components/rest/RestListActionDelete'
import RestListColumnActions from '@/common/components/rest/RestListColumnActions'
import { listIdentifierLabel } from '@/modules/audiences/enum/ListRecordEnums'
import { SportsLabel } from '@/modules/integrations/types/sportradar/enum/SportsLabelEnum'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { validateIdentifier } from '@/modules/audiences/utils'
import _ from 'lodash'
import RestListEditable from '@/common/components/rest/RestListEditable'
import ListCreateDialog from '@/modules/audiences/components/ListCreateDialog'
import RestListActionView from '@/common/components/rest/RestListActionView'
import { integrationsTypes } from '@/modules/integrations/enum/IntegrationsEnum'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'

export default {
  components: {
    RestListColumnSettings,
    RestListColumnDate,
    RestListActionView,
    ListCreateDialog,
    RestListEditable,
    RestListColumn,
    RestListColumnEnum,
    RestListActionDelete,
    RestListColumnActions
  },

  props: {
    suppress: {
      type: Number,
      required: true
    },
    externalType: {
      type: String,
      required: false,
      default: null
    }
  },

  data() {
    return {
      createPopup: false,
      listIdentifierLabel,
      SportsLabel,
      selectedColumns: {}
    }
  },

  mounted() {
    EventBus.$on(this.createEventName, () => {
      this.openCreatePopup()
    })
  },

  destroyed() {
    EventBus.$off(this.createEventName)
  },

  computed: {
    createEventName() {
      if (this.suppress) {
        return 'create-suppression-list'
      } else if (this.isOptimoveList) {
        return 'create-optimove-list'
      } else {
        return 'create-user-list'
      }
    },

    rules() {
      return {
        identifier: {
          required: true,
          trigger: 'blur'
        },
        name: {
          required: true,
          trigger: 'blur'
        }
      }
    },

    fetchEndpoint() {
      const params = {
        suppress: this.suppress,
        external_type: this.externalType,
        sort: 'name',
        page: 1
      }

      if (this.isSportradarList) {
        params.expand =
          'start,sport,country,competition,competitor_home,competitor_away'
      }

      return projectEndpoint({
        method: 'get',
        url: `lists`,
        params: params
      })
    },

    readonly() {
      return !this.$can('updateProjectItem')
    },

    features() {
      return this.$store.state.app.features || {}
    },

    identifierList() {
      let filtered = Object.keys(this.listIdentifierLabel).filter(key =>
        validateIdentifier(key, this.features)
      )
      if (!this.$store.state.app.features.customerIdEnabled) {
        filtered = filtered.filter(item => item !== 'customer_id')
      }
      return _.pick(this.listIdentifierLabel, filtered)
    },

    isSportradarList() {
      return this.externalType === integrationsTypes.sportradar
    },

    isOptimoveList() {
      return this.externalType === integrationsTypes.optimove
    },

    columns() {
      return {
        start: {
          title: this.$t('List_Field_Start'),
          defaultValue: true,
          validation: true
        },
        country: {
          title: this.$t('List_Field_Country'),
          defaultValue: true,
          validation: true
        },
        competition: {
          title: this.$t('List_Field_Competition'),
          defaultValue: true,
          validation: true
        },
        competitor_home: {
          title: this.$t('List_Field_Home'),
          defaultValue: true,
          validation: true
        },
        competitor_away: {
          title: this.$t('List_Field_Away'),
          defaultValue: true,
          validation: true
        }
      }
    }
  },

  methods: {
    rowViewRoute(row) {
      if (this.suppress) {
        return projectRoute({
          name: 'AudienceSuppressionListItems',
          query: { list_id: row.id }
        })
      } else if (this.isSportradarList) {
        return projectRoute({
          name: 'AudienceSportradarListItems',
          query: { list_id: row.id }
        })
      } else if (this.isOptimoveList) {
        return projectRoute({
          name: 'AudienceOptimoveListItems',
          query: { list_id: row.id }
        })
      } else {
        return projectRoute({
          name: 'AudienceUserListItems',
          query: { list_id: row.id }
        })
      }
    },

    rowUpdateRoute(row) {
      return projectEndpoint({
        method: 'patch',
        url: `lists/${row.id}`
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `lists/${row.id}`
      })
    },

    openCreatePopup() {
      this.createPopup = true
    },

    closeCreatePopup() {
      this.createPopup = false
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/modules/audiences/pages/BaseLists.vue